import { useCallback, FC, useState, useEffect, useRef, useMemo, useLayoutEffect } from 'react';
import ReactDOM from 'react-dom';
import dayjs from 'dayjs';
import {
  StyledDateRangeContainer,
  StyledDateRangeContent,
  StyledDateRangeFilter,
  StyledDateRangeSelect,
  FilterButtonChip,
  FilterButtonGroup,
} from './DateRangeFilter.styles';
import { AllieButton, AllieSelect, AllieText } from '../../../components';
import { DateRangeFilterProps } from './DateRangeFilter.types';
import { useOutsideAlerter } from '../../../common/hooks';
import './DateRangeFilter.css';

const namespace = 'date-range-filter';

export const AllieDateRangeFilter: FC<DateRangeFilterProps> = ({
  selected,
  onSelect,
  staticOpts = [],
  selectedRange = [],
  dateRangePickerOpts = {},
  customRangeLabel = 'custom',
  title = 'Periodicity',
  closeText = 'Close',
  confirmText = 'Confirm',
  isMobile = false,
  placeholder,
  toLeft = false,
  position = 'below',
  onCalendarSelect,
  onFilterCancel,
  autoApply = false,
  noDefaultSelection = false,
  showMobileOptions = true,
  fullWidth,
  'data-testid': dataTestId,
}) => {
  const [rangeEl, setRangeEl] = useState<any>();
  const [selection, setSelection] = useState<Date[]>(selectedRange);
  const [showFilter, setShowFilter] = useState<boolean>(false);
  const [isCalendarVisible, setIsCalendarVisible] = useState<boolean>(!!selectedRange);
  const [showCalendarMobile, setShowCalendarMobile] = useState<boolean>(false);
  const [isClosing, setIsClosing] = useState<boolean>(false);
  const filterRef = useRef<HTMLDivElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const clickCountRef = useRef<number>(0);
  const closingTimerRef = useRef<ReturnType<typeof setTimeout>>();

  const selectionDoneSmooth = useCallback(
    (value: string | Date[]) => {
      setIsClosing(true);
      closingTimerRef.current = setTimeout(() => {
        setIsClosing(false);
        onSelect(value);
        setShowFilter(false);
        setShowCalendarMobile(false);
      }, 350);
    },
    [onSelect]
  );

  useEffect(() => {
    setSelection(selectedRange);
  }, [selectedRange]);

  useEffect(() => {
    return () => { if (closingTimerRef.current) clearTimeout(closingTimerRef.current); };
  }, []);

  const selectionDone = useCallback(
    (value: string | Date[]) => {
      onSelect(value);
      setShowFilter(false);
      setShowCalendarMobile(false);
    },
    [onSelect]
  );

  const hideCalendar = useCallback(() => {
    document
      .getElementsByClassName('rdrDateRangeWrapper')[0]
      ?.setAttribute('style', 'opacity: 0; position: absolute; right: 9999px;');

    if (toLeft) {
      document
        .getElementsByClassName('rdrDateRangePickerWrapper')[0]
        ?.setAttribute('style', 'position: absolute; left: 0;');
    }
  }, []);

  const showCalendar = useCallback(() => {
    document.getElementsByClassName('rdrDateRangeWrapper')[0]?.setAttribute('style', 'opacity: 1;');

    if (toLeft) {
      document
        .getElementsByClassName('rdrDateRangePickerWrapper')[0]
        ?.setAttribute('style', 'position: absolute; left: -300px;');
    }
  }, []);

  const toggleCalendar = useCallback(() => {
    document
      .getElementsByClassName('rdrDateRangeWrapper')[0]
      ?.getAttribute('style')
      ?.includes('opacity: 0;')
      ? showCalendar()
      : hideCalendar();
  }, []);

  const handleClose = useCallback(() => {
    hideCalendar();
    onFilterCancel?.();
    clickCountRef.current = 0;

    setSelection(selectedRange);
    setShowFilter(false);
    setShowCalendarMobile(false);
  }, [selectedRange]);

  useOutsideAlerter(wrapperRef, () => {
    handleClose();
  });

  useLayoutEffect(() => {
    if (showFilter) {
      const wrapper = document.getElementsByClassName('rdrDateRangeWrapper')[0];
      if (wrapper) {
        selected && hideCalendar();
        selectedRange.length && setSelection(selectedRange);
        setRangeEl(wrapper);
      }
    }
  }, [showFilter]);

  const selectedText = useMemo(() => {
    const targetStaticOpt = staticOpts.find((o) => o.value === selected)?.label;
    if (targetStaticOpt) return targetStaticOpt;
    if (selectedRange.length)
      return `${dayjs(selectedRange[0]).format('YYYY-MM-DD')} - ${dayjs(selectedRange[1]).format(
        'YYYY-MM-DD'
      )}`;

    return '';
  }, [selected, selectedRange]);

  const renderMobileView = () => (
    <FilterButtonGroup>
      {staticOpts.map((opt) => (
        <FilterButtonChip
          key={opt.value}
          isSelected={selected === opt.value}
          onClick={() => selectionDone(opt.value)}
          corner="rounded"
          color={{
            backgroundColor: selected === opt.value ? 'primary-80' : 'neutral-5',
            borderColor: selected === opt.value ? 'transparent' : 'neutral-30',
          }}
        >
          <AllieText type="body2" color={selected === opt.value ? 'neutral-10' : 'light-10'}>
            {opt.label}
          </AllieText>
        </FilterButtonChip>
      ))}
      <FilterButtonChip
        isSelected={!staticOpts.some((opt) => opt.value === selected)}
        onClick={() => {
          setShowCalendarMobile(!showCalendarMobile);
          onCalendarSelect?.();
        }}
        corner="rounded"
        color={{
          backgroundColor: !staticOpts.some((opt) => opt.value === selected)
            ? 'primary-80'
            : 'neutral-5',
          borderColor: !staticOpts.some((opt) => opt.value === selected)
            ? 'transparent'
            : 'neutral-30',
        }}
      >
        <AllieText
          type="body2"
          color={!staticOpts.some((opt) => opt.value === selected) ? 'neutral-10' : 'light-10'}
        >
          {customRangeLabel}
        </AllieText>
      </FilterButtonChip>
    </FilterButtonGroup>
  );

  return (
    <StyledDateRangeContainer isMobile={isMobile} fullWidth={fullWidth} data-testid={dataTestId}>
      <StyledDateRangeContent isMobile={isMobile}>
        {isMobile ? (
          <>
            {showMobileOptions && renderMobileView()}
            {showCalendarMobile && (
              <div
                ref={filterRef}
                data-no-selection={noDefaultSelection && !selection.length ? '' : undefined}
                className={isClosing ? 'date-range-filter--closing' : ''}
              >
                <StyledDateRangeFilter
                  isMobile={isMobile}
                  onChange={(item: any) => {
                    let newSelection: Date[];
                    if (noDefaultSelection && !selection.length) {
                      const clicked = item.selection.startDate;
                      newSelection = [clicked, clicked];
                    } else {
                      newSelection = [item.selection.startDate, item.selection.endDate];
                    }
                    setSelection(newSelection);
                    if (autoApply) {
                      clickCountRef.current += 1;
                      if (clickCountRef.current >= 2) {
                        clickCountRef.current = 0;
                        selectionDoneSmooth(newSelection);
                      }
                    }
                  }}
                  showDateDisplay={false}
                  showPreview={false}
                  retainEndDateOnFirstSelection
                  preventSnapRefocus
                  calendarFocus={'backwards'}
                  months={1}
                  ranges={[
                    {
                      startDate: selection[0] || new Date(),
                      endDate: selection[1] || new Date(),
                      key: 'selection',
                    },
                  ]}
                  direction={'vertical'}
                  staticRanges={[]}
                  inputRanges={[]}
                  {...dateRangePickerOpts}
                />
                {!autoApply && (
                  <div
                    id="mobile-calendar-footer"
                    style={{ textAlign: 'center', paddingBottom: 5 }}
                  >
                    <AllieButton
                      style={{ marginRight: 7.5 }}
                      allieSize="small"
                      color={'light'}
                      corner="rounded-edges"
                      onClick={handleClose}
                      data-testid="period-picker-close"
                    >
                      {closeText}
                    </AllieButton>
                    <AllieButton
                      allieSize="small"
                      corner="rounded-edges"
                      onClick={() => selectionDone(selection)}
                      data-testid="period-picker-apply"
                    >
                      {confirmText}
                    </AllieButton>
                  </div>
                )}
              </div>
            )}
          </>
        ) : (
          <div ref={wrapperRef} data-open={showFilter}>
            <StyledDateRangeSelect fullWidth={fullWidth}>
              <div className={`${namespace}__item`}>
                <AllieText
                  className={`${namespace}__label`}
                  type="body3"
                  weight={400}
                  color="neutral-50"
                >
                  {title}
                </AllieText>
                <AllieSelect
                  className={`${namespace}__item__select`}
                  placeholder={placeholder}
                  options={[]}
                  color={{
                    backgroundColor: 'white',
                    allieColor: 'neutral-90',
                  }}
                  onClick={() => setShowFilter(!showFilter)}
                  selectedText={selectedText}
                  selected={selected as any}
                  interfaceOptions={{
                    cssClass: `${namespace}__interface`,
                    arrow: false,
                    showBackdrop: false,
                  }}
                />
              </div>
            </StyledDateRangeSelect>
            {showFilter && (
              <div
                style={position === 'above' ? { position: 'absolute', bottom: '45px' } : {}}
                ref={filterRef}
                onMouseDown={(e) => e.stopPropagation()}
                data-no-selection={noDefaultSelection && !selection.length ? '' : undefined}
                className={isClosing ? 'date-range-filter--closing' : ''}
              >
            <StyledDateRangeFilter
              isMobile={isMobile}
              toLeft={toLeft}
              onChange={(item: any) => {
                switch (item.selection.key) {
                  case 'selection': {
                    let newSelection: Date[];
                    if (noDefaultSelection && !selection.length) {
                      const clicked = item.selection.startDate;
                      newSelection = [clicked, clicked];
                    } else {
                      newSelection = [item.selection.startDate, item.selection.endDate];
                    }
                    setSelection(newSelection);
                    if (autoApply) {
                      clickCountRef.current += 1;
                      if (clickCountRef.current >= 2) {
                        clickCountRef.current = 0;
                        selectionDoneSmooth(newSelection);
                      }
                    }
                    break;
                  }
                  case 'showRange': {
                    toggleCalendar();
                    break;
                  }
                  default: {
                    hideCalendar();
                    setSelection([]);
                    selectionDone(item.selection.key);
                    break;
                  }
                }
              }}
              showDateDisplay={false}
              retainEndDateOnFirstSelection
              preventSnapRefocus
              calendarFocus={'backwards'}
              months={2}
              ranges={[
                {
                  startDate: selection[0] || new Date(),
                  endDate: selection[1] || new Date(),
                  key: 'selection',
                },
              ]}
              direction={'horizontal'}
              renderStaticRangeLabel={(range: any) => (
                <span data-testid={`period-picker-${range.range().key}`}>{range.label}</span>
              )}
              inputRanges={[]}
              staticRanges={staticOpts
                ?.map((o) => ({
                  label: o.label,
                  hasCustomRendering: true,
                  range: () => ({
                    startDate: new Date(),
                    endDate: new Date(),
                    key: o.value,
                  }),
                  isSelected: () => o.selected,
                }))
                .concat([
                  {
                    label: customRangeLabel,
                    hasCustomRendering: true,
                    range: () => ({
                      startDate: selectedRange[0] || new Date(),
                      endDate: selectedRange[1] || new Date(),
                      key: 'showRange',
                    }),
                    isSelected: () => selectedRange.length > 0 && !selected,
                  },
                ])}
              {...dateRangePickerOpts}
            />
            {!autoApply && rangeEl &&
              ReactDOM.createPortal(
                <div style={{ textAlign: 'center', paddingBottom: 5 }}>
                  <AllieButton
                    style={{ marginRight: 7.5 }}
                    allieSize="small"
                    color={'light'}
                    corner="rounded-edges"
                    onClick={handleClose}
                    data-testid="period-picker-close"
                  >
                    {closeText}
                  </AllieButton>
                  <AllieButton
                    allieSize="small"
                    corner="rounded-edges"
                    onClick={() => selectionDone(selection)}
                    data-testid="period-picker-apply"
                  >
                    {confirmText}
                  </AllieButton>
                </div>,
                rangeEl
              )}
            </div>
          )}
          </div>
        )}
      </StyledDateRangeContent>
    </StyledDateRangeContainer>
  );
};
